import { useState, useCallback, useRef } from 'react';
import preqinService from '../services/preqinService';
import { PreqinQuickSearchParams } from '../types/preqin';

interface UsePreqinSearchResult {
    results: any[];
    loading: boolean;
    error: string | null;
    quickSearch: (query: string) => Promise<void>;
}

const usePreqinSearch = (): UsePreqinSearchResult => {
    const [results, setResults] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const lastQueryRef = useRef<string>('');

    const quickSearch = useCallback(async (query: string) => {
        //console.log('usePreqinSearch: quickSearch called with query:', query);
        const trimmedQuery = query.trim();
        
        // Abort previous request if exists
        if (abortControllerRef.current) {
            //console.log('usePreqinSearch: Aborting previous request');
            abortControllerRef.current.abort();
        }

        if (!trimmedQuery) {
            setResults([]);
            setError(null);
            setLoading(false);
            lastQueryRef.current = '';
            return;
        }
        if (trimmedQuery.length < 3) {
            setResults([]);
            setError(null);
            setLoading(false);
            return;
        }
        // Don't search if it's the same query as last time
        if (trimmedQuery === lastQueryRef.current) {
            return;
        }
        lastQueryRef.current = trimmedQuery;
        setLoading(true);
        setError(null);
        // Create new abort controller for this request
        abortControllerRef.current = new AbortController();

        try {
            const params: PreqinQuickSearchParams = { query: trimmedQuery };
            const data = await preqinService.quickSearch(params);

            // Only update if this is still the current request
            if (!abortControllerRef.current.signal.aborted) {
                // Handle different response structures
                if (data?.data) {
                    setResults(Array.isArray(data.data) ? data.data : [data.data]);
                } else if (Array.isArray(data)) {
                    setResults(data);
                } else {
                    setResults([data]);
                }
            }

        } catch (err: any) {
            // Only handle error if request wasn't aborted
            if (!abortControllerRef.current?.signal.aborted) {
                //console.error('Preqin search error:', err);
                setError(err instanceof Error ? err.message : 'An error occurred while searching Preqin data');
                setResults([]);
            }
        } finally {
            // Only set loading to false if request wasn't aborted
            if (!abortControllerRef.current?.signal.aborted) {
                setLoading(false);
            }
        }
    }, []);

    return {
        results,
        loading,
        error,
        quickSearch
    };
};

export default usePreqinSearch;